import { FC, memo, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiActions,
  MiContentDrawer,
  MiDrawerHeader,
} from "@miplatform/components";
import {
  APP_BAR_HEIGHT,
  APP_BAR_HEIGHT_TABLET,
  HTTP_STATUSES,
  MIBUILD_PAGE_HEADER_HEIGHT,
} from "@miplatform/constants";
import { IHome } from "@miplatform/types";
import { mdiClose, mdiHomeOutline } from "@mdi/js";
import { HomeAndAccountSearch } from "@mibuildweb/components";
import { useWorkOrder, useWorkOrderTemplates } from "@mibuildweb/hooks";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import {
  CUSTOM_WORK_ORDER_NAME,
  CreateCustomWorkOrderForm,
  CreateWorkOrderForm,
  CustomWorkTemplateBase,
} from "./CreateWorkOrderDrawer.constants";
import { CustomWorkOrderCreation } from "./CustomWorkOrderCreation";
import { WorkOrderCreation } from "./WorkOrderCreation";
import { WorkOrderTemplateSelection } from "./WorkOrderTemplateSelection";

interface CreateWorkOrderDrawerProps {
  open: boolean;
  customWorkTemplateBase?: CustomWorkTemplateBase;
  onRefresh?: () => void;
  onClose: () => void;
}

export const CreateWorkOrderDrawer: FC<CreateWorkOrderDrawerProps> = memo(
  ({ open = false, customWorkTemplateBase, onRefresh, onClose }) => {
    const theme = useTheme();
    const { classes } = useStyles();
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));
    const [selectedHome, setSelectedHome] = useState<IHome | null>(null);
    const [selectedTemplate, setSelectedTemplate] =
      useState<CustomWorkTemplateBase | null>(null);
    const DRAWER_TOP_OFFSET =
      (isTablet ? APP_BAR_HEIGHT_TABLET : APP_BAR_HEIGHT) +
      MIBUILD_PAGE_HEADER_HEIGHT;
    const {
      homeWorkOrderTemplates,
      isLoading: isLoadingTemplates,
      setHomeWorkOrderTemplates,
      getHomeWorkOrderTemplates,
    } = useWorkOrderTemplates();
    const { isLoading: isCreatingWorkOrder, createWorkOrder } = useWorkOrder();
    const workTemplateBases = useMemo(
      () =>
        customWorkTemplateBase
          ? [
              { ...customWorkTemplateBase, name: CUSTOM_WORK_ORDER_NAME },
              ...homeWorkOrderTemplates,
            ]
          : homeWorkOrderTemplates,
      [customWorkTemplateBase, homeWorkOrderTemplates]
    );
    const isLoading = isLoadingTemplates || isCreatingWorkOrder;

    const getWorkOrderTemplates = async (homeId: string) => {
      const { status, data } = await getHomeWorkOrderTemplates({ id: homeId });

      if (status === HTTP_STATUSES.OK) {
        setHomeWorkOrderTemplates(data);
      }
    };

    const handleClose = () => {
      setSelectedHome(null);
      setSelectedTemplate(null);
      onClose();
    };

    const getDueDateTime = (
      data: CreateWorkOrderForm | CreateCustomWorkOrderForm
    ) =>
      data.dueDate
        ?.clone()
        .set({
          hour: data.dueDateTime?.hour() ?? 0,
          minute: data.dueDateTime?.minute() ?? 0,
        })
        .toISOString();

    const handleCreateWorkOrder = async (data: CreateWorkOrderForm) => {
      if (!selectedHome || !selectedTemplate) {
        return;
      }
      const dueDate = getDueDateTime(data);

      const { status } = await createWorkOrder({
        homeId: selectedHome.id,
        workOrderTemplateId: selectedTemplate.id,
        hardDueDate: dueDate,
        orderId: selectedTemplate.orderId,
        notes: data.notes,
      });

      if (status === HTTP_STATUSES.OK) {
        handleClose();
      }
    };

    const handleCreateCustomWorkOrder = async (
      data: CreateCustomWorkOrderForm
    ) => {
      if (!selectedHome || !selectedTemplate) {
        return;
      }
      const dueDate = getDueDateTime(data);

      const { status } = await createWorkOrder({
        ...data,
        workOrderDuration: parseInt(data.workOrderDuration, 10),
        homeId: selectedHome.id,
        workOrderTemplateId: selectedTemplate.id,
        hardDueDate: dueDate,
      });

      if (status === HTTP_STATUSES.OK) {
        handleClose();
        onRefresh?.();
      }
    };

    useEffect(() => {
      if (selectedHome) {
        getWorkOrderTemplates(selectedHome.id);
      }
    }, [selectedHome]);

    return (
      <MiContentDrawer
        isLoading={isLoading}
        open={open}
        onClose={handleClose}
        hideBackdrop
        sx={{
          "& > .MuiPaper-root": {
            top: DRAWER_TOP_OFFSET,
            height: `calc(100% - ${DRAWER_TOP_OFFSET}px)`,
          },
        }}
      >
        <Box className={classes.root}>
          {selectedHome && selectedTemplate && (
            <MiDrawerHeader
              title={selectedHome?.address.addressLine1 || ""}
              subtitle={[
                selectedHome
                  ? `${selectedHome.address.city}, ${selectedHome.address.regionCode}`
                  : "",
                selectedHome?.community ?? "",
                selectedHome?.builder ?? "",
              ]}
              leftIcon={mdiHomeOutline}
              rightAction={
                <MiActions
                  type="ICONS"
                  actions={[
                    {
                      name: "Close",
                      icon: mdiClose,
                      onClick: handleClose,
                    },
                  ]}
                />
              }
            />
          )}
          {(!selectedHome || !selectedTemplate) && (
            <MiDrawerHeader
              title="Create New Work Order"
              rightAction={
                <MiActions
                  type="ICONS"
                  actions={[
                    {
                      name: "Close",
                      icon: mdiClose,
                      onClick: handleClose,
                    },
                  ]}
                />
              }
            />
          )}
          <Box className={classes.content}>
            {!selectedHome && (
              <Box className={classes.search}>
                <HomeAndAccountSearch
                  hideCreateNewOption
                  showLogo
                  onSelect={(option: IHome) => {
                    setSelectedHome(option);
                  }}
                />
              </Box>
            )}
            {selectedHome && !selectedTemplate && (
              <WorkOrderTemplateSelection
                home={selectedHome}
                isLoading={isLoading}
                templates={workTemplateBases as CustomWorkTemplateBase[]}
                onSelectTemplate={(template: CustomWorkTemplateBase) => {
                  setSelectedTemplate(template);
                }}
                onBack={() => setSelectedHome(null)}
              />
            )}
            {selectedHome && selectedTemplate && !selectedTemplate.isCustom && (
              <WorkOrderCreation
                template={selectedTemplate}
                isLoading={isLoading}
                onSubmit={handleCreateWorkOrder}
                onClose={handleClose}
              />
            )}
            {selectedHome && selectedTemplate && selectedTemplate.isCustom && (
              <CustomWorkOrderCreation
                home={selectedHome}
                template={selectedTemplate}
                isLoading={isLoading}
                onSubmit={handleCreateCustomWorkOrder}
                onClose={handleClose}
              />
            )}
          </Box>
        </Box>
      </MiContentDrawer>
    );
  }
);

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
  },
  content: {
    height: "100%",
  },
  search: {
    padding: theme.spacing(20, 13),
  },
}));
